import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dog, Lock, User, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { login } from '../services/api';

export const LoginPage = ({ onLogin }) => {
  const [form, setForm] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.username || !form.password) {
      toast.error('Complete todos los campos');
      return;
    }
    setLoading(true);
    try {
      const response = await login(form);
      localStorage.setItem('benivet_token', response.data.token);
      localStorage.setItem('benivet_user', JSON.stringify(response.data.user));
      toast.success(`Bienvenido, ${response.data.user.full_name}`);
      onLogin(response.data.user);
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error de autenticación');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#E6F7F4] via-white to-[#F5F7FA] p-4" data-testid="login-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src="/logo.png" 
            alt="Benivet" 
            className="w-24 h-24 object-contain mx-auto mb-4"
          />
          <h1 className="text-3xl font-extrabold text-[#004F5D] tracking-tight">Benivet</h1>
          <p className="text-slate-500 mt-1">Ops Manager</p>
        </div>

        <Card className="border-0 shadow-2xl shadow-[#00AE95]/10">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl text-center text-[#004F5D]">Iniciar Sesión</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label className="text-[#004F5D] font-semibold">Usuario</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    type="text"
                    value={form.username}
                    onChange={(e) => setForm({ ...form, username: e.target.value })}
                    className="pl-11 h-12 rounded-xl border-slate-200 focus:border-[#00AE95] focus:ring-[#00AE95]/20"
                    placeholder="Tu nombre de usuario"
                    data-testid="username-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-[#004F5D] font-semibold">Contraseña</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="pl-11 pr-11 h-12 rounded-xl border-slate-200 focus:border-[#00AE95] focus:ring-[#00AE95]/20"
                    placeholder="Tu contraseña"
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="text-right">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-[#00AE95] hover:text-[#009A84] hover:underline"
                >
                  ¿Olvidaste tu contraseña?
                </button>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 rounded-xl font-semibold text-white shadow-lg shadow-[#00AE95]/30 hover:shadow-xl hover:shadow-[#00AE95]/40 transition-all"
                style={{ background: 'linear-gradient(135deg, #00AE95 0%, #009A84 100%)' }}
                data-testid="login-btn"
              >
                {loading ? 'Entrando...' : 'Entrar'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Forgot Password Modal */}
        {showForgotPassword && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-sm border-0 shadow-2xl">
              <CardHeader>
                <CardTitle className="text-lg text-[#004F5D]">Recuperar Contraseña</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-slate-600 text-sm">
                  Para restablecer tu contraseña, contacta con el administrador del sistema:
                </p>
                <div className="p-4 bg-[#E6F7F4] rounded-xl">
                  <p className="font-semibold text-[#004F5D]">info@benivetbenissa.com</p>
                  <p className="text-sm text-slate-500 mt-1">o habla directamente con tu supervisor</p>
                </div>
                <Button
                  onClick={() => setShowForgotPassword(false)}
                  className="w-full bg-[#004F5D] hover:bg-[#003845]"
                >
                  Entendido
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        <p className="text-center text-slate-400 text-sm mt-6">
          Benivet Clínica Veterinaria · Benissa
        </p>
      </div>
    </div>
  );
};
